import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import {
    Users,
    Menu,
    X,
    ArrowRight,
    Star,
    ChevronDown,
    UserPlus,
    Shield,
    AlertCircle,
    Zap,
    TrendingUp,
    Globe,
    Key,
    Sparkle,
} from 'lucide-react';

// Импорт страниц
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import ResetPasswordPage from './pages/ResetPasswordPage';

import { authService } from './services/authService';

// НОВЫЙ ИМПОРТ: AuthContext
import { AuthProvider, useAuth } from './contexts/AuthContext';

// НОВЫЙ ИМПОРТ: Компонент рассылки
import BroadcastAdmin from './components/BroadcastAdmin';

// Компонент для обработки различных типов реферальных ссылок
const ReferralHandler = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const pathSegments = location.pathname.split('/').filter(Boolean);

    let refCode = null;

    refCode = params.get('ref') || params.get('referral') || params.get('invite');

    if (!refCode && pathSegments.length >= 2) {
        if (pathSegments[0] === 'ref' || pathSegments[0] === 'invite') {
            refCode = pathSegments[1];
        }
    }

    if (!refCode && pathSegments.length >= 2 && pathSegments[0] === 'r') {
        refCode = pathSegments[1];
    }

    if (refCode) {
        return <Navigate to={`/auth?ref=${refCode}`} replace />;
    }

    return <InviteInfoPage />;
};

// Страница с информацией о том, как получить приглашение
const InviteInfoPage = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-mint-light to-white flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-blue-primary/10 rounded-full flex items-center justify-center">
                    <Users className="text-blue-primary" size={40} />
                </div>

                <h1 className="text-2xl font-bold text-gray-800 mb-4">
                    Нужно приглашение
                </h1>

                <p className="text-gray-600 mb-6">
                    Эта ссылка недействительна. Для регистрации в ЛАЙТ необходима персональная реферальная ссылка от действующего участника.
                </p>

                <div className="space-y-3">
                    <Link to="/" className="block">
                        <button className="w-full bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-medium">
                            На главную страницу
                        </button>
                    </Link>

                    <Link to="/auth" className="block">
                        <button className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                            У меня уже есть аккаунт
                        </button>
                    </Link>
                </div>
            </div>
        </div>
    );
};

// ✅ ИСПРАВЛЕНО: AdminRoute использует useAuth вместо запроса
const AdminRoute = ({ children }) => {
    const { user, loading } = useAuth(); // Используем данные из контекста

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-primary"></div>
            </div>
        );
    }

    const isAdmin = user?.is_admin ||
        user?.role === 'admin' ||
        user?.login?.toLowerCase() === 'admin' ||
        user?.login === 'Admin';

    console.log('Current user:', user);
    console.log('User login:', user?.login);
    console.log('Is admin?', isAdmin);

    if (!isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Доступ запрещен</h1>
                    <p className="text-gray-600 mb-2">У вас нет прав для доступа к админ-панели</p>
                    <p className="text-sm text-gray-500 mb-6">Ваш логин: {user?.login || 'не определен'}</p>
                    <Link to="/dashboard">
                        <button className="bg-blue-primary text-white px-6 py-2 rounded-lg hover:bg-blue-primary/80 transition-colors">
                            Вернуться в кабинет
                        </button>
                    </Link>
                </div>
            </div>
        );
    }

    return children;
};

// ✅ ИСПРАВЛЕНО: ProtectedRoute использует useAuth вместо запроса
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth(); // Используем данные из контекста

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-primary"></div>
            </div>
        );
    }

    return isAuthenticated ? children : <Navigate to="/auth" replace />;
};

// ✅ ИСПРАВЛЕНО: HomePage использует useAuth
const HomePage = ({ showJoinModal, setShowJoinModal }) => {
    const { isAuthenticated, user } = useAuth(); // Используем данные из контекста
    const [counter, setCounter] = useState(1247);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');

    useEffect(() => {
        const interval = setInterval(() => {
            setCounter(prev => prev + Math.floor(Math.random() * 3) + 1);
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveSection(sectionId);
        }
        setIsMenuOpen(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-mint-light to-white text-gray-800 w-full max-w-full">
            {/* Декоративные элементы */}
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-mint-light/10 to-blue-primary/5 -z-10"></div>

            {/* Хедер */}
            <header className="relative z-40 bg-white/80 backdrop-blur-md shadow-sm sticky top-0">
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center py-4">
                        {/* Логотип и счетчик */}
                        <div className="flex items-center space-x-6">
                            <Link to="/" className="flex items-center space-x-3">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-mint-light to-blue-primary/20 flex items-center justify-center shadow-lg">
                                    <img
                                        src="/images/logo.png"
                                        alt="ЛАЙТ логотип"
                                        className="w-8 h-8 object-contain"
                                    />
                                </div>
                                <span className="text-2xl font-bold text-blue-primary">ЛАЙТ</span>
                            </Link>

                            <div className="hidden md:flex items-center bg-blue-primary/10 px-3 py-2 rounded-full">
                                <Users size={16} className="text-blue-primary mr-2"/>
                                <span className="text-blue-primary font-semibold text-sm">{counter.toLocaleString()}+ участников</span>
                            </div>
                        </div>

                        {/* Кнопки авторизации/личный кабинет */}
                        <div className="hidden md:flex items-center space-x-4">
                            {isAuthenticated ? (
                                <div className="flex items-center space-x-3">
                                    {user && (
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 rounded-full bg-blue-primary/10 flex items-center justify-center text-blue-primary font-semibold text-sm mr-2">
                                                {user.avatar ? (
                                                    <img src={user.avatar} alt={user.login} className="w-8 h-8 rounded-full object-cover" />
                                                ) : (
                                                    user.login.charAt(0).toUpperCase()
                                                )}
                                            </div>
                                            <span className="text-sm font-medium text-gray-700">{user.login}</span>
                                        </div>
                                    )}
                                    <Link to="/dashboard">
                                        <button className="bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white px-6 py-2 rounded-full hover:shadow-lg transition-all duration-300 font-medium">
                                            Личный кабинет
                                        </button>
                                    </Link>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-3">
                                    <Link to="/auth">
                                        <button className="text-blue-primary hover:text-blue-primary/80 transition-colors px-4 py-2 rounded-lg hover:bg-blue-primary/5">
                                            Войти
                                        </button>
                                    </Link>
                                    <button
                                        onClick={() => setShowJoinModal(true)}
                                        className="bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white px-6 py-2 rounded-full hover:shadow-lg transition-all duration-300 font-medium"
                                    >
                                        Присоединиться
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Мобильное меню кнопка */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="text-blue-primary p-2 hover:bg-blue-primary/5 rounded-lg transition-colors"
                            >
                                {isMenuOpen ? <X size={28}/> : <Menu size={28}/>}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Мобильное меню */}
            <div
                className={`fixed top-0 right-0 h-screen bg-white w-64 z-50 transform transition-transform duration-300 pt-20 px-6 shadow-2xl ${
                    isMenuOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <div className="flex items-center mb-8 bg-blue-primary/10 p-3 rounded-lg">
                    <Users size={18} className="text-blue-primary mr-2"/>
                    <span className="text-blue-primary font-medium">{counter}+ участников</span>
                </div>

                {isAuthenticated ? (
                    <div className="mt-8 space-y-3">
                        {user && (
                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                <div className="w-10 h-10 rounded-full bg-blue-primary/10 flex items-center justify-center text-blue-primary font-semibold mr-3">
                                    {user.avatar ? (
                                        <img src={user.avatar} alt={user.login} className="w-10 h-10 rounded-full object-cover" />
                                    ) : (
                                        user.login.charAt(0).toUpperCase()
                                    )}
                                </div>
                                <div>
                                    <p className="font-medium text-gray-800">{user.login}</p>
                                </div>
                            </div>
                        )}
                        <Link to="/dashboard" className="w-full">
                            <button className="w-full bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white py-3 px-6 rounded-full hover:shadow-lg transition-all duration-300 font-medium flex justify-center items-center">
                                <span>Личный кабинет</span>
                            </button>
                        </Link>
                    </div>
                ) : (
                    <div className="mt-8 space-y-3">
                        <Link to="/auth" className="block">
                            <button className="w-full text-blue-primary border border-blue-primary py-3 px-6 rounded-full hover:bg-blue-primary/5 transition-all duration-300 font-medium">
                                Войти
                            </button>
                        </Link>
                        <button
                            onClick={() => {
                                setShowJoinModal(true);
                                setIsMenuOpen(false);
                            }}
                            className="w-full bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white py-3 px-6 rounded-full hover:shadow-lg transition-all duration-300 font-medium"
                        >
                            Присоединиться
                        </button>
                    </div>
                )}
            </div>

            {/* Модальное окно для присоединения */}
            {showJoinModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                        {/* Заголовок */}
                        <div className="bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Users className="mr-3" size={24} />
                                    <h3 className="text-xl font-semibold">Присоединиться к ЛАЙТ</h3>
                                </div>
                                <button
                                    onClick={() => setShowJoinModal(false)}
                                    className="text-white/80 hover:text-white transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Контент */}
                        <div className="p-6">
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 mx-auto mb-4 bg-blue-primary/10 rounded-full flex items-center justify-center">
                                    <UserPlus className="text-blue-primary" size={32} />
                                </div>
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                                    Регистрация только по приглашениям
                                </h4>
                                <p className="text-gray-600 text-sm">
                                    Платформа ЛАЙТ работает как закрытое сообщество. Чтобы присоединиться,
                                    вам необходимо получить персональную ссылку от действующего участника.
                                </p>
                            </div>

                            <div className="space-y-4 mb-6">
                                <div className="flex items-start">
                                    <div className="w-6 h-6 rounded-full bg-blue-primary/20 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-primary text-sm font-bold">1</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-800">Найдите участника</p>
                                        <p className="text-xs text-gray-600">Свяжитесь с знакомым, который уже является участником ЛАЙТ</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-6 h-6 rounded-full bg-blue-primary/20 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-primary text-sm font-bold">2</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-800">Получите ссылку</p>
                                        <p className="text-xs text-gray-600">Попросите личную реферальную ссылку для регистрации</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-6 h-6 rounded-full bg-blue-primary/20 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-primary text-sm font-bold">3</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-800">Зарегистрируйтесь</p>
                                        <p className="text-xs text-gray-600">Перейдите по ссылке и создайте аккаунт</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-mint-light/30 rounded-lg p-4 mb-6">
                                <div className="flex items-center mb-2">
                                    <Shield className="text-mint-medium mr-2" size={18} />
                                    <span className="text-sm font-medium text-gray-800">Почему так?</span>
                                </div>
                                <p className="text-xs text-gray-600">
                                    Это обеспечивает качество сообщества и позволяет поддерживать доверительные отношения между участниками.
                                </p>
                            </div>
                        </div>

                        {/* Кнопки */}
                        <div className="p-6 pt-0 space-y-3">
                            <Link to="/auth" className="block">
                                <button
                                    onClick={() => setShowJoinModal(false)}
                                    className="w-full bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-medium"
                                >
                                    У меня есть ссылка
                                </button>
                            </Link>
                            <button
                                onClick={() => setShowJoinModal(false)}
                                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                            >
                                Понятно
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Основной контент главной страницы */}
            <main className="pt-8 pb-20">
                {/* Hero секция */}
                <section id="home" className="container mx-auto px-4 flex flex-col-reverse md:flex-row items-center">
                    <div className="md:w-1/2 mt-12 md:mt-0">
                        <div className="bg-blue-primary/10 px-4 py-2 rounded-full inline-flex items-center mb-4">
                            <Zap size={16} className="text-blue-primary mr-2"/>
                            <span className="text-blue-primary font-medium">Новая бизнес-экосистема</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-primary to-blue-primary/70 bg-clip-text text-transparent leading-tight">
                            Откройте новые <br/>
                            горизонты успеха <br/>
                            с нами
                        </h1>
                        <p className="text-lg md:text-xl mb-8 max-w-md text-gray-700">
                            Присоединяйтесь к растущему сообществу единомышленников,
                            создающих бизнес-пространство будущего. Вместе мы откроем
                            новые возможности роста.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/auth">
                                <button className="bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white py-3 px-10 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-medium">
                                    Начать сейчас
                                </button>
                            </Link>
                            {/*<a href="https://light-platform.com/presentation.pdf" target="_blank" className="border-2 border-blue-primary text-blue-primary py-3 px-8 rounded-full hover:bg-blue-primary/5 transition-colors text-lg font-medium flex items-center justify-center">*/}
                            {/*    <span>Смотреть презентацию</span>*/}
                            {/*</a>*/}
                        </div>

                        <div className="mt-12 grid grid-cols-3 gap-4">
                            <div className="text-center">
                                <div className="text-blue-primary font-bold text-3xl">{counter}+</div>
                                <div className="text-gray-600 text-sm">Участников</div>
                            </div>
                            <div className="text-center">
                                <div className="text-blue-primary font-bold text-3xl">96%</div>
                                <div className="text-gray-600 text-sm">Довольных партнеров</div>
                            </div>
                            <div className="text-center">
                                <div className="text-blue-primary font-bold text-3xl">24/7</div>
                                <div className="text-gray-600 text-sm">Поддержка</div>
                            </div>
                        </div>
                    </div>

                    {/* Правая часть Hero секции с логотипом */}
                    <div className="md:w-1/2 flex justify-center">
                        <div className="relative w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96">
                            {/* Добавляем внешний светящийся круг */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-primary/10 via-mint-medium/30 to-blue-primary/20 rounded-full opacity-70 animate-pulse blur-lg"></div>

                            {/* Основной фон */}
                            <div className="absolute inset-0 bg-gradient-to-br from-mint-light via-mint-medium to-blue-primary/30 rounded-full opacity-30 animate-pulse"></div>

                            {/* Контейнер логотипа с эффектом свечения */}
                            <div className="absolute inset-8 bg-white rounded-full shadow-xl flex items-center justify-center overflow-hidden">
                                {/* Свечение за логотипом */}
                                <div className="absolute inset-0 bg-gradient-to-r from-mint-light via-mint-medium to-blue-primary/20 opacity-40 animate-spin-slowest"></div>

                                {/* Логотип с увеличенным размером */}
                                <div className="relative z-10 w-4/5 h-4/5 flex items-center justify-center transform hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/logo.png"
                                        alt="ЛАЙТ логотип"
                                        className="w-full h-full object-contain drop-shadow-lg"
                                    />
                                </div>
                            </div>

                            {/* Орбитальные элементы */}
                            <div className="absolute w-full h-full animate-spin-slow">
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow">
                                    <div className="bg-blue-primary/10 w-10 h-10 rounded-full flex items-center justify-center">
                                        <Users size={20} className="text-blue-primary"/>
                                    </div>
                                </div>
                            </div>

                            <div className="absolute w-full h-full animate-spin-slower" style={{animationDelay: "0.5s"}}>
                                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow">
                                    <div className="bg-blue-primary/10 w-10 h-10 rounded-full flex items-center justify-center">
                                        <TrendingUp size={20} className="text-blue-primary"/>
                                    </div>
                                </div>
                            </div>

                            <div className="absolute w-full h-full animate-spin-slowest" style={{animationDelay: "1s"}}>
                                <div className="absolute top-1/2 left-0 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow">
                                    <div className="bg-blue-primary/10 w-10 h-10 rounded-full flex items-center justify-center">
                                        <Globe size={20} className="text-blue-primary"/>
                                    </div>
                                </div>
                            </div>

                            <div className="absolute w-full h-full animate-spin-slower" style={{animationDelay: "1.5s"}}>
                                <div className="absolute top-1/2 right-0 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow">
                                    <div className="bg-blue-primary/10 w-10 h-10 rounded-full flex items-center justify-center">
                                        <Key size={20} className="text-blue-primary"/>
                                    </div>
                                </div>
                            </div>

                            {/* Добавляем искры вокруг логотипа */}
                            {/*<Sparkle delay={0.2} top={10} left={40} size={5}/>*/}
                            {/*<Sparkle delay={1.0} top={80} left={60} size={4}/>*/}
                            {/*<Sparkle delay={0.7} top={45} left={90} size={6}/>*/}
                            {/*<Sparkle delay={1.3} top={30} left={15} size={4}/>*/}
                        </div>
                    </div>
                </section>

                {/* Здесь можно добавить другие секции главной страницы */}
            </main>

            {/* Подвал */}
            <footer className="bg-blue-primary text-white py-12">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between">
                        <div className="mb-8 md:mb-0">
                            <div className="flex items-center mb-6">
                                <div className="w-10 h-10 rounded-full overflow-hidden mr-3 bg-white flex items-center justify-center">
                                    <span className="text-blue-primary font-medium">Л</span>
                                </div>
                                <span className="text-xl font-medium tracking-wider">ЛАЙТ</span>
                            </div>
                            <p className="max-w-xs">
                                Создаем возможности для амбициозных людей, открывая новые горизонты бизнеса.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                            <div>
                                <h4 className="text-lg font-medium mb-4">Компания</h4>
                                <ul className="space-y-2">
                                    <li><a href="#" className="hover:text-mint-light transition-colors">О нас</a></li>
                                    <li><a href="#" className="hover:text-mint-light transition-colors">Команда</a></li>
                                    <li><a href="#" className="hover:text-mint-light transition-colors">Карьера</a></li>
                                </ul>
                            </div>

                            <div>
                                <h4 className="text-lg font-medium mb-4">Партнерство</h4>
                                <ul className="space-y-2">
                                    <li><a href="#" className="hover:text-mint-light transition-colors">Возможности</a></li>
                                    <li><a href="#" className="hover:text-mint-light transition-colors">Обучение</a></li>
                                    <li><a href="#" className="hover:text-mint-light transition-colors">FAQ</a></li>
                                </ul>
                            </div>

                            <div>
                                <h4 className="text-lg font-medium mb-4">Контакты</h4>
                                <ul className="space-y-2">
                                    <li>info@лайт.рф</li>
                                    <li>+7 (123) 456-78-90</li>
                                    <li>Москва, ул. Примерная, 123</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-white/20 mt-12 pt-6 flex flex-col md:flex-row justify-between items-center">
                        <p>© 2025 ЛАЙТ. Все права защищены.</p>
                        <div className="mt-4 md:mt-0">
                            <ul className="flex space-x-6">
                                <li><a href="/politiks_and_rules.pdf" className="hover:text-mint-light transition-colors">Политика конфиденциальности и Условия</a></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

// ✅ ИСПРАВЛЕНО: AppContent использует useAuth
function AppContent() {
    const { login, logout, refreshUser } = useAuth(); // Используем методы из контекста
    const [showJoinModal, setShowJoinModal] = useState(false);

    // ✅ УДАЛЕНО: Весь код проверки авторизации - теперь это в AuthContext

    const handleLogin = async () => {
        // После логина обновляем данные пользователя
        await refreshUser();
    };

    const handleLogout = () => {
        logout();
    };

    return (
        <Routes>
            {/* Главная страница */}
            <Route path="/" element={
                <HomePage
                    showJoinModal={showJoinModal}
                    setShowJoinModal={setShowJoinModal}
                />
            } />

            {/* Обработка всех типов реферальных ссылок */}
            <Route path="/ref/:refCode" element={<ReferralHandler />} />
            <Route path="/invite/:refCode" element={<ReferralHandler />} />
            <Route path="/invite" element={<ReferralHandler />} />
            <Route path="/r/:refCode" element={<ReferralHandler />} />

            {/* Авторизация/регистрация */}
            <Route path="/auth" element={<AuthPage onLogin={handleLogin} />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Админ-рассылка */}
            <Route path="/admin/broadcast" element={
                <ProtectedRoute>
                    <AdminRoute>
                        <BroadcastAdmin />
                    </AdminRoute>
                </ProtectedRoute>
            } />

            {/* Защищенные маршруты */}
            <Route path="/dashboard/*" element={
                <ProtectedRoute>
                    <Dashboard onLogout={handleLogout} />
                </ProtectedRoute>
            } />

            {/* 404 и неправильные ссылки */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// ✅ ГЛАВНЫЙ КОМПОНЕНТ: Оборачиваем все в AuthProvider
function App() {
    useEffect(() => {
        const checkVersion = async () => {
            try {
                const response = await fetch('/meta.json?' + new Date().getTime());
                const data = await response.json();
                const currentVersion = localStorage.getItem('app_version');

                if (currentVersion && currentVersion !== data.version) {
                    console.log('Обнаружена новая версия, обновление...');
                    localStorage.setItem('app_version', data.version);
                    window.location.reload(true);
                } else if (!currentVersion) {
                    localStorage.setItem('app_version', data.version);
                }
            } catch (error) {
                console.error('Ошибка проверки версии:', error);
            }
        };

        checkVersion();
    }, []);

    return (
        <Router>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </Router>
    );
}

export default App;
